import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, Input, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessengerFileOpener, openableFile, opensByItsLink} from '../file-open';
import {FileMark, fileMarkOf, formatFileSize, isImageFile, isVideoFile, MessageFile} from '../message-files';

/**
 * A FILE IN A BUBBLE (ITP-E-194-10), after carbon's `.itp-chat-file` and `.itp-msgr-image`. A file is a row: a 44px
 * mark (the glyph over the format word, keyed off the extension), the name truncated, the size when known; with a
 * link it downloads. An image is its preview instead, at 240px 4:3, opening full size; one whose picture does not load
 * falls back to the row, and one with no picture at all (a fixture) is carbon's tinted block.
 *
 * B-19 (ITP-E-207-07): a chat file names no link. It is opened by asking the server for one at the moment it is opened
 * (`MessengerCardsPort.downloadLink`, minted after the server checks the reader may see its message and good for a few
 * minutes), never from a link kept on the page: a picture or a PDF opens in the browser, a video plays here, any other
 * kind downloads. An image's preview is drawn from a link minted when its card is drawn.
 *
 * ITP-E-240-10: every file opens through `MessengerFileOpener`, the action Details' Shared calls too, with the same file, so a
 * file opens or downloads the same way from both. A link keeps its address for a middle-click, the context menu and
 * assistive technology, and reads as Details' links do (a new tab, `noopener noreferrer`, no `download`); a plain click is
 * the action's, which cancels the link's own so one click never opens twice.
 */
@Component({
  standalone: true,
  selector: 'messenger-file-card',
  imports: [CommonModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './messenger-file-card.component.html',
  styleUrls: ['./messenger-file-card.component.scss']
})
export class MessengerFileCardComponent implements OnDestroy {
  private readonly opener = inject(MessengerFileOpener);
  private readonly changes = inject(ChangeDetectorRef);
  private readonly fileChanged = new Subject<void>();

  @Input() set file(file: MessageFile) {
    this.fileChanged.next();
    this.current = file;
    this.mark = fileMarkOf(file);
    this.size = formatFileSize(file.size);
    this.image = isImageFile(file);
    this.video = isVideoFile(file);
    this.chatFile = !!file.attachmentId && !file.url;
    this.preview = this.image ? file.thumbnail || file.url || null : null;
    this.previewFailed = false;
    this.playing = null;
    this.openFailed = false;
    if (this.image && !this.preview && this.chatFile) {
      this.drawPreview();
    }
  }

  /** Whether the bubble is this user's own: the mark takes the blue ground's tint. */
  @Input() @HostBinding('class.mine') mine = false;

  current: MessageFile;
  mark: FileMark;
  size = '';
  image = false;
  video = false;
  /** B-19: a chat file, opened through a link minted when it is opened. */
  chatFile = false;
  preview: string | null = null;
  previewFailed = false;
  /** B-19: the link a chat video plays from, once it was opened here. */
  playing: string | null = null;
  opening = false;
  openFailed = false;

  onPreviewError(): void {
    this.previewFailed = true;
  }

  /**
   * ITP-E-240-10: a click on the file opens it through the one open action, a chat file through a link minted now; a chat
   * video plays in the card instead. A click with a modifier on a file with an address is the browser's, from its link.
   */
  open(event: MouseEvent): void {
    if (!this.chatFile && opensByItsLink(event)) {
      return;
    }
    event.preventDefault();
    if (this.opening) {
      return;
    }
    this.opening = true;
    this.openFailed = false;
    if (this.chatFile && this.video) {
      this.play();
      return;
    }
    this.opener.open(openableFile(this.current)).pipe(takeUntil(this.fileChanged)).subscribe({
      next: () => this.settle(false),
      error: () => this.settle(true)
    });
  }

  ngOnDestroy(): void {
    this.fileChanged.next();
    this.fileChanged.complete();
  }

  // B-19: a chat video plays here, from a link minted for it now.
  private play(): void {
    this.opener.link(this.current.attachmentId).pipe(takeUntil(this.fileChanged)).subscribe({
      next: link => {
        this.playing = link.previewUrl || link.url;
        this.settle(false);
      },
      error: () => this.settle(true)
    });
  }

  private settle(failed: boolean): void {
    this.opening = false;
    this.openFailed = failed;
    this.changes.markForCheck();
  }

  // A picture of a chat file: drawn from a link minted for it now; a card whose link cannot be had falls back to the row.
  private drawPreview(): void {
    this.opener.link(this.current.attachmentId).pipe(takeUntil(this.fileChanged)).subscribe({
      next: link => {
        this.preview = link.previewUrl || link.url;
        this.changes.markForCheck();
      },
      error: () => {
        this.previewFailed = true;
        this.changes.markForCheck();
      }
    });
  }
}
